import { useTranslation } from "react-i18next";
import { Check, Plus, ArrowUpRight } from "lucide-react";
import type { NowConnection } from "../../api/now";
import { NOW_LINK_ICON } from "../../lib/nowModes";
import { cn } from "../../lib/utils";

interface Props {
  connection: NowConnection;
  brought: boolean;
  /** False once two others are brought in. */
  canBring: boolean;
  /** A hand-off or tool is opening: Open waits. */
  busy: boolean;
  onToggle: () => void;
  onOpen: () => void;
}

const ICON_BTN =
  "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 disabled:pointer-events-none";

/**
 * One thing the person already has, with why it connects (spec 2026-11-04 §2.3 part 1).
 * A quiet row, not a card: label and reason, with small icon actions that keep a 44px hit area.
 */
const ConnectionCard = ({ connection: c, brought, canBring, busy, onToggle, onOpen }: Props) => {
  const { t } = useTranslation();
  const Icon = NOW_LINK_ICON[c.kind];
  return (
    <li className="flex items-center gap-2 py-1.5">
      <Icon className="h-3.5 w-3.5 shrink-0 text-primary/80" aria-hidden />
      <div className="min-w-0 flex-1">
        <p dir="auto" className="line-clamp-1 break-words text-sm text-foreground text-start" title={c.label}>
          {c.label}
        </p>
        {c.why && (
          <p dir="auto" className="line-clamp-2 break-words text-xs text-muted-foreground text-start">
            {c.why}
          </p>
        )}
      </div>
      {/* A quality has no page of its own to land on (followUpTarget sends it to bare /portal). */}
      {c.kind !== "trait" && (
        <button
          type="button"
          className={ICON_BTN}
          disabled={busy}
          onClick={onOpen}
          aria-label={t("now.dive.open")}
          title={t("now.dive.open")}
        >
          <ArrowUpRight className="h-4 w-4 rtl:-scale-x-100" aria-hidden />
        </button>
      )}
      <button
        type="button"
        className={cn(ICON_BTN, brought && "text-primary bg-primary/10 hover:bg-primary/15")}
        aria-pressed={brought}
        disabled={!canBring}
        onClick={onToggle}
        aria-label={t(brought ? "now.dive.brought" : "now.dive.bring")}
        title={t(brought ? "now.dive.brought" : "now.dive.bring")}
      >
        {brought ? <Check className="h-4 w-4" aria-hidden /> : <Plus className="h-4 w-4" aria-hidden />}
      </button>
    </li>
  );
};

export default ConnectionCard;
